import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";
import { Customers } from "./AppStateTypes";

type User = {
  id: string;
  name: string;
  email: string;
  avatar?: string;
};

interface AppContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  roleName: string;
  setRoleName: (role: string) => void;
  customer: string;
  setCustomer: (c: string) => void;
  adminCustomers: Customers;
  ladId: number | null;
  setLadId: (id: number | null) => void;

  // states from Facility.jsx
  locations: Array<any>; // Define proper type if available
  setLocations: (locs: Array<any>) => void;
  location: string;
  setLocation: (loc: string) => void;
  systems: Array<any>; // Define proper type if available
  setSystems: (sys: Array<any>) => void;
  system: string;
  setSystem: (sys: string) => void;
  errorMessage: string;
  setErrorMessage: (msg: string) => void;
  systemsLoading: boolean;
  setSystemsLoading: (loading: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [roleName, setRoleName] = useState<string>(() => localStorage.getItem("role_name") || "");
  const [adminCustomers, setAdminCustomers] = useState<Customers>([]);
  const [customer, setCustomer] = useState("");
  const [ladId, setLadId] = useState<number | null>(null);

  const [locations, setLocations] = useState<Array<any>>([]);
  const [location, setLocation] = useState<string>("");
  const [systems, setSystems] = useState<Array<any>>([]);
  const [system, setSystem] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [systemsLoading, setSystemsLoading] = useState<boolean>(false);

    const selectedLocation = locations.find((loc) => loc.plant_name === location);
  const selectedLocationId = selectedLocation?.id || null;

  useEffect(() => {
    if (roleName === "Admin") {
      const fetchAdminCustomers = async () => {
        try {
          const accessToken = sessionStorage.getItem("accessToken");
          const id = localStorage.getItem("id") || "";
          const userId = parseInt(id);

          const headers = accessToken ? { Authorization: `${accessToken}` } : {};
          const response = await axios.post(`${baseUrl}/customer`, { userId }, { headers });

          const customerList = response?.data || [];
          setAdminCustomers(customerList);

          if (customerList.length > 0) {
            const storedCustomerId = localStorage.getItem("customerId");
            if (storedCustomerId) {
              const storedCustomer = customerList.find(
                (c) => c.id === parseInt(storedCustomerId)
              );
              setCustomer(storedCustomer ? storedCustomer.name : customerList[0].name);
            } else {
              setCustomer(customerList[0].name);
            }
          }
        } catch (error) {
          console.error("Error fetching admin customer data:", error);
        }
      };

      fetchAdminCustomers();
    }
  }, [baseUrl, roleName]);

  useEffect(() => {
    const fetchFinalLADId = async () => {
      try {
        const locationId = selectedLocationId ?? localStorage.getItem("selectedLocationId");
        if (!selectedLocationId) return;

        const accessToken = sessionStorage.getItem("accessToken");
        const headers = accessToken ? { Authorization: `${accessToken}` } : {};
        const response = await axios.get(`${baseUrl}/explorer/location/${locationId}/lad`, {
          headers,
        });

        const ladIdFromApi = response?.data?.data || null;
        if (ladIdFromApi) setLadId(ladIdFromApi);
        localStorage.setItem("ladId", ladIdFromApi);
      } catch (error) {
        console.error("Error fetching final LAD ID:", error);
      }
    };

    fetchFinalLADId();
  }, [baseUrl, selectedLocationId]);

  return (
    <AppContext.Provider
      value={{
        user,
        setUser,
        isLoading,
        setIsLoading,
        roleName,
        setRoleName,
        customer,
        setCustomer,
        adminCustomers,
        ladId,
        setLadId,
        locations,
        setLocations,
        location,
        setLocation,
        systems,
        setSystems,
        system,
        setSystem,
        errorMessage,
        setErrorMessage,
        systemsLoading,
        setSystemsLoading,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use context
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
